import {
  CALLING_COUNTRY_CODES,
  type CallingCountryCode,
  type CallingCountryCodeAlpha3,
} from '@wix/form-validator';
import localeDataset from '@wix/locale-dataset-javascript';
import parsePhoneNumber, {
  AsYouType,
  isValidPhoneNumber,
} from 'libphonenumber-js';
import Metadata from 'libphonenumber-js/metadata.min.json';

export type Country = {
  callingCode: string;
  countryName: string;
  longCountryCode: CallingCountryCodeAlpha3;
  shortCountryCode: CallingCountryCode;
};

export const PHONE_PREFIX = '+';

export function parseCountryCodeFromPhoneNumber(
  phoneNumber: string,
): CallingCountryCode | undefined {
  const parsedNumber = phoneNumber ? parsePhoneNumber(phoneNumber) : undefined;
  const parsedCountryCode = parsedNumber?.country;
  if (parsedCountryCode) {
    return parsedCountryCode as CallingCountryCode;
  }

  const asYouType = new AsYouType();
  phoneNumber && asYouType.input(phoneNumber);
  const guessedCountryCode = asYouType.getCountry();
  return guessedCountryCode as CallingCountryCode;
}

export function parseCountryCodeFromCallingCode(
  callingCode: string,
): CallingCountryCode | undefined {
  const countryByCallingCode = getCountryCodeByCallingCode(callingCode);

  return countryByCallingCode as CallingCountryCode;
}

export function toLongCountryCode(
  shortCountryCode: string | undefined,
): CallingCountryCodeAlpha3 | undefined {
  return shortCountryCode
    ? CALLING_COUNTRY_CODES[shortCountryCode as CallingCountryCode]
    : undefined;
}

export function getCallingCode(
  countryCode: CallingCountryCodeAlpha3 | undefined,
) {
  return countryCode && localeDataset.getCountryByKey(countryCode)?.dialCode;
}

function getCountryCodeByCallingCode(phoneNumber: string): string | undefined {
  phoneNumber = phoneNumber.trim();

  if (!phoneNumber.startsWith(PHONE_PREFIX)) {
    return undefined;
  }

  const withoutPrefix = phoneNumber.replace(/^\+/, '');
  const oneCharCode = withoutPrefix.substring(0, 1);
  const twoCharCode = withoutPrefix.substring(0, 2);
  const threeCharCode = withoutPrefix.substring(0, 3);
  const fourCharCode = withoutPrefix.substring(0, 4);

  const country =
    Metadata.country_calling_codes[fourCharCode]?.[0] ||
    Metadata.country_calling_codes[threeCharCode]?.[0] ||
    Metadata.country_calling_codes[twoCharCode]?.[0] ||
    Metadata.country_calling_codes[oneCharCode]?.[0];

  return country;
}

export function replaceCallingCode(phoneNumber: string, callingCode: string) {
  const countryCodeByPhoneNumber = parseCountryCodeFromPhoneNumber(phoneNumber);
  const countryCodeByCallingCode = parseCountryCodeFromCallingCode(phoneNumber);
  const countryCode = toLongCountryCode(
    countryCodeByPhoneNumber || countryCodeByCallingCode,
  );
  const currentCallingCode = getCallingCode(countryCode);

  if (currentCallingCode) {
    return phoneNumber.replace(currentCallingCode, callingCode);
  } else {
    const withoutPrefix = phoneNumber.replace(PHONE_PREFIX, '');
    return `${callingCode}${withoutPrefix}`;
  }
}

export function parseLocalPhone(
  value: string | null,
  country?: CallingCountryCode,
) {
  if (value && country) {
    try {
      const phoneNumber = parsePhoneNumber(value, country);
      const formattedPhoneNumber = phoneNumber?.formatInternational();
      if (formattedPhoneNumber && isValidPhoneNumber(formattedPhoneNumber)) {
        return formattedPhoneNumber.replace(/\s+/g, '');
      }
    } catch (e) {}
  }

  return value;
}
